import { useMemo, useRef, useState } from 'react';
import {
  InlineLoading,
  InlineNotification,
  Modal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { sum } from 'lodash-es';

import { fromDateParam } from 'utils/dates';

import { draftAssetChangePendingAtom } from '../atoms';
import { usePackageWizardCtx } from '../context';
import { useDraftAssetChange } from '../draft-build-hooks';
import { useOwnershipChanges } from '../ownership-changes-hooks';
import {
  ALL_ADDED_INCLUDED,
  namedOwnershipChanges,
  type OwnershipChangesTab,
} from '../ownership-changes-utils';
import type { OwnershipChangesResponse } from '../types';
import { counted, formatCount } from '../utils';
import { OwnershipChangesTable } from './ownership-changes-table';
import styles from './ownership-changes-modal.module.scss';

/** The review's tabs, in order. */
const TABS: OwnershipChangesTab[] = ['added', 'removed'];

export interface OwnershipChangesModalProps {
  draftId: string;
  asOfDate: string | null;
  /** The tab it opens on: Assets added when there are any. */
  initialTab: OwnershipChangesTab;
  /** The step is rebuilding (a scenario switch): nothing can be added until it is done. */
  held: boolean;
  onClose: () => void;
}

/**
 * The summary strip's two counts.
 *
 * @param {OwnershipChangesResponse} changes - Both lists
 * @param {number} available - Assets added not yet in the table
 * @return {object} Assets removed and Assets added, as the strip words them
 */
const summaryOf = (
  { removed, added }: OwnershipChangesResponse,
  available: number
) => {
  const addedState =
    available > 0
      ? `${formatCount(available)} available to include`
      : added.length > 0
        ? 'all included'
        : 'none';
  return {
    removed: `${formatCount(removed.length)} removed · ${counted(
      sum(removed.map((row) => row.propertyCount)),
      'property',
      'properties'
    )} excluded`,
    added: `${formatCount(added.length)} added · ${addedState}`,
  };
};

/**
 * "Asset ownership changes": a clone's Assets added and Assets removed on its
 * date, read when it opens. Ticked Assets added join the Assets table through
 * "Add selected", which waits as any page 2 change that builds does, then
 * closes; a refusal says why here. Assets removed are read only. Cancel or
 * close drops the ticks. Rendered only while open.
 *
 * @param {OwnershipChangesModalProps} props - The draft, its date, the tab to open on, whether the step holds it, and close
 * @return {JSX.Element} The modal
 */
export function OwnershipChangesModal({
  draftId,
  asOfDate,
  initialTab,
  held,
  onClose,
}: OwnershipChangesModalProps) {
  const { form } = usePackageWizardCtx();
  const changeAssets = useDraftAssetChange(form);
  // Another change on its way, or the step rebuilding: the server would refuse this one.
  const busy = useAtomValue(draftAssetChangePendingAtom) || held;
  const { data: changes, error: readError } = useOwnershipChanges(draftId);
  const named = useMemo(
    () => changes && namedOwnershipChanges(changes),
    [changes]
  );

  const [tab, setTab] = useState(initialTab);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [adding, setAdding] = useState(false);
  // Read by the close handler, which Carbon keeps from its first render for Escape.
  const addingRef = useRef(false);
  const [addError, setAddError] = useState<string | null>(null);

  // Only rows not yet in the table go, as a re-read may have moved one in.
  const toAdd = (changes?.added ?? [])
    .filter((row) => !row.included && selectedIds.has(row.salesforceId))
    .map((row) => row.salesforceId);

  /**
   * Sends the ticked Assets added through the page 2 change: a refusal stays
   * here with its reason; anything else closes the review once settled, the
   * step showing how the build ended.
   *
   * @return {Promise<void>} Settles with the change
   */
  const handleAdd = async (): Promise<void> => {
    addingRef.current = true;
    setAdding(true);
    setAddError(null);
    try {
      await changeAssets({ draftId, addedAssetIds: toAdd });
    } catch (error) {
      setAddError(error instanceof Error ? error.message : String(error));
      return;
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
    onClose();
  };

  const available = changes?.added.filter((row) => !row.included).length ?? 0;
  const summary = changes && summaryOf(changes, available);

  return (
    <Modal
      open
      size="lg"
      hasScrollingContent
      className={styles.modal}
      modalHeading="Asset ownership changes"
      primaryButtonText={`Add selected (${formatCount(toAdd.length)})`}
      primaryButtonDisabled={toAdd.length === 0 || busy}
      loadingStatus={adding ? 'active' : 'inactive'}
      loadingDescription="Adding assets…"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleAdd}
      // The step waits on the build Add selected queued; the review stays until it is done.
      onRequestClose={() => {
        if (!addingRef.current) onClose();
      }}
      preventCloseOnClickOutside
    >
      <p className={styles.intro}>
        Review changes based on the selected As of Date
        {asOfDate ? ` (${fromDateParam(asOfDate)})` : ''}. Removed assets are
        excluded; added assets can be included in the package.
      </p>
      {addError !== null && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className={styles.notification}
          title="Couldn't add the selected assets"
          subtitle={addError}
        />
      )}
      {readError !== null && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className={styles.notification}
          title="Couldn't read the asset ownership changes"
          subtitle={readError}
        />
      )}
      {named === null ? (
        readError === null && (
          <InlineLoading description="Reading asset ownership changes…" />
        )
      ) : (
        <>
          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Assets removed</span>
              <span className={styles.summaryValue}>{summary?.removed}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Assets added</span>
              <span className={styles.summaryValue}>{summary?.added}</span>
            </div>
          </div>
          <Tabs
            selectedIndex={TABS.indexOf(tab)}
            onChange={({ selectedIndex }: { selectedIndex: number }) =>
              setTab(TABS[selectedIndex])
            }
          >
            <TabList aria-label="Asset ownership changes">
              <Tab>{`Assets added (${formatCount(named.added.length)})`}</Tab>
              <Tab>
                {`Assets removed (${formatCount(named.removed.length)})`}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                {named.added.length === 0 ? (
                  <p className={styles.empty}>
                    No added assets are available to include.
                  </p>
                ) : (
                  <>
                    <p className={styles.helper}>
                      {toAdd.length > 0
                        ? `${counted(toAdd.length, 'asset', 'assets')} selected`
                        : 'Select added assets to include in the new package.'}
                    </p>
                    <OwnershipChangesTable
                      rows={named.added}
                      selection={{
                        selectedIds,
                        onSelectedIdsChange: setSelectedIds,
                        disabled: adding || busy,
                      }}
                    />
                    {available === 0 && (
                      <p className={styles.empty}>{ALL_ADDED_INCLUDED}</p>
                    )}
                  </>
                )}
              </TabPanel>
              <TabPanel>
                {named.removed.length === 0 ? (
                  <p className={styles.empty}>
                    No assets were removed as of the selected date.
                  </p>
                ) : (
                  <>
                    <p className={styles.helper}>
                      These assets were removed and cannot be included in the
                      new package.
                    </p>
                    <OwnershipChangesTable rows={named.removed} />
                  </>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </>
      )}
    </Modal>
  );
}
